import { Fragment, type ReactNode, useMemo, useState } from 'react';

import { type NoteBlock, parseNotes } from './notesFormat';

// NotesView: Notizen als Einträge (neueste zuerst) mit Kopf, Abschnitten, Listen und Feldern; der neueste Eintrag ist offen,
// ältere und doppelte sind eingeklappt (Dominik 14.10.2026: „das muss schöner strukturiert werden“).
export function NotesView({ notes }: { notes: string | null | undefined }) {
  const entries = useMemo(() => parseNotes(notes), [notes]);
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  if (entries.length === 0) return <p className="muted small">Noch keine Notizen.</p>;
  return (
    <ol className="note-entries">
      {entries.map((e, i) => {
        const long = e.blocks.length > 4 || e.length > 400;
        const openByDefault = i === 0 && !e.duplicateOf;
        const open = !long || (toggled[e.key] ?? openByDefault);
        return (
          <li key={e.key} className={`note-entry${e.report ? ' note-entry-report' : ''}${e.duplicateOf ? ' note-entry-duplicate' : ''}`}>
            <header className="note-head">
              {e.when && <time>{e.when.replace(' ', ' · ')}</time>}
              {e.author && <strong>{e.author}</strong>}
              {e.report && <span className="badge badge-info">Terminbericht</span>}
              {e.appointment && <span className="badge badge-muted">Termin {e.appointment.replace(',', '')}</span>}
            </header>
            {e.duplicateOf && <p className="muted small note-duplicate">Inhalt steht auch im Eintrag vom {e.duplicateOf}.</p>}
            <div className={open ? 'note-body' : 'note-body note-body-clamped'}>
              <NoteBlocks blocks={open ? e.blocks : e.blocks.slice(0, 3)} />
            </div>
            {long && (
              <button type="button" className="link-button small" onClick={() => setToggled({ ...toggled, [e.key]: !open })}>
                {open ? 'Weniger anzeigen' : 'Ganzen Eintrag anzeigen'}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// NoteBlocks: aufeinanderfolgende Listenpunkte bzw. Felder werden zusammengefasst.
function NoteBlocks({ blocks }: { blocks: NoteBlock[] }) {
  const out: ReactNode[] = [];
  for (let i = 0; i < blocks.length; ) {
    const b = blocks[i];
    if (!b) break;
    if (b.kind === 'item' || b.kind === 'field') {
      const run: NoteBlock[] = [];
      while (i < blocks.length && blocks[i]?.kind === b.kind) run.push(blocks[i++] as NoteBlock);
      out.push(
        b.kind === 'item' ? (
          <ul key={`l${i}`} className="note-list">
            {run.map((x, k) => (x.kind === 'item' ? <li key={k}>{x.text}</li> : null))}
          </ul>
        ) : (
          <dl key={`f${i}`} className="note-fields">
            {run.map((x, k) =>
              x.kind === 'field' ? (
                <Fragment key={k}>
                  <dt>{x.label}</dt>
                  <dd>{x.text}</dd>
                </Fragment>
              ) : null,
            )}
          </dl>
        ),
      );
      continue;
    }
    switch (b.kind) {
      case 'heading':
        out.push(<h4 key={i}>{b.text}</h4>);
        break;
      case 'sub':
        out.push(<h5 key={i}>{b.text}</h5>);
        break;
      case 'rule':
        out.push(<hr key={i} />);
        break;
      default:
        out.push(<p key={i}>{b.text}</p>);
    }
    i++;
  }
  return <>{out}</>;
}
